import { useAuth as useSupabaseAuth } from '../contexts/AuthContext';
import { useCallback, useEffect, useMemo, useState } from 'react';

export interface AuthUser {
  userId: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  imageUrl?: string;
}

export const useAuth = () => {
  const { user, session, loading, getAccessToken, signOut } = useSupabaseAuth();
  const [authToken, setAuthToken] = useState<string | null>(null);

  // Stable reference - many useEffects depend on apiCall; recreating it every render caused fetch loops (429 storms).
  const sessionAccessToken = session?.access_token ?? null;
  const userId = user?.id ?? null;

  /** Auth bootstrap done, user resolved, AND a usable token is available. */
  const authReady = Boolean(!loading && !!user && !!(authToken || sessionAccessToken));

  // Get JWT token for API calls and refresh periodically
  useEffect(() => {
    const fetchToken = async () => {
      if (user) {
        try {
          const token = await getAccessToken();
          setAuthToken(token);
        } catch (error) {
          console.error('Failed to get auth token:', error);
          setAuthToken(null);
        }
      } else {
        setAuthToken(null);
      }
    };

    if (!loading) {
      fetchToken();
    }

    let refreshInterval: NodeJS.Timeout;
    if (user && !loading) {
      refreshInterval = setInterval(async () => {
        try {
          const token = await getAccessToken();
          setAuthToken(token);
        } catch (error) {
          console.error('Periodic token refresh failed:', error);
        }
      }, 50 * 60 * 1000);
    }

    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [user, loading, getAccessToken]);

  const getAuthHeaders = (): Record<string, string> => {
    if (!authToken) {
      return {};
    }
    return {
      'Authorization': `Bearer ${authToken}`
    };
  };

  const apiCall = useCallback(async (url: string, options: RequestInit = {}) => {
    // ONLY force HTTPS for non-localhost URLs (production)
    const isLocalhost = url.includes('localhost') || url.includes('127.0.0.1');
    const secureUrl = isLocalhost ? url : url.replace(/^http:/, 'https:');
    if (secureUrl !== url) {
      console.warn('⚠️ FORCED HTTP → HTTPS:', url, '→', secureUrl);
    }

    // Determine timeout based on endpoint
    // Transcription needs 6 minutes (360s) - 5 min max audio + 1 min processing buffer
    const isTranscriptionRequest = url.includes('/transcribe');
    const isRecordingDownload = url.includes('/recording/download');
    // The AI template draft runs a reasoning model and can exceed the 60s
    // default; give it the same generous window as the gateway proxy.
    const isAiDraft = url.includes('/templates/draft-from-jd');
    const timeoutMs = isTranscriptionRequest
      ? 360000
      : isRecordingDownload
        ? 600000
        : isAiDraft
          ? 120000
          : 60000; // 10 min for MP4 export, 6 min transcribe, else 1 min

    // Function to make the actual API call with a token
    const makeRequest = async (token: string | null) => {
      const authHeaders: Record<string, string> = {};
      if (token) {
        authHeaders['Authorization'] = `Bearer ${token}`;
      }

      const callerHeaders = (options.headers as Record<string, string>) || {};
      const callerHasContentType = Object.keys(callerHeaders).some(
        (k) => k.toLowerCase() === 'content-type',
      );
      const headers: Record<string, string> = {
        ...authHeaders,
        ...callerHeaders,
        'ngrok-skip-browser-warning': 'true',
      };

      // Only default Content-Type to JSON when the caller didn't
      // set one and the body isn't FormData. The recording chunk
      // uploads explicitly send `application/octet-stream` and we
      // must not clobber that with `application/json`, otherwise
      // some proxies/CDNs may reject the raw bytes.
      if (!(options.body instanceof FormData) && !callerHasContentType) {
        headers['Content-Type'] = 'application/json';
      }

      // Create AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

      try {
        const response = await fetch(secureUrl, {
          ...options,
          headers,
          signal: controller.signal,
        });
        clearTimeout(timeoutId);
        return response;
      } catch (error: any) {
        clearTimeout(timeoutId);
        if (error.name === 'AbortError') {
          console.error(`⏱️ Request timeout after ${timeoutMs / 1000}s:`, url);
        }
        throw error;
      }
    };

    // Prefer React state, fall back to session, then force-fetch as last resort
    let currentToken = authToken || sessionAccessToken || null;
    if (!currentToken) {
      try {
        const freshToken = await getAccessToken();
        if (freshToken) {
          currentToken = freshToken;
          setAuthToken(currentToken);
        }
      } catch (error) {
        console.error('Failed to get fresh auth token:', error);
      }
    }

    // Make the first attempt
    let response = await makeRequest(currentToken);

    // If we get a 401, always try to refresh - don't gate on stale userId closure
    if (response.status === 401) {
      try {
        const freshToken = await getAccessToken();
        if (freshToken) {
          setAuthToken(freshToken);
          response = await makeRequest(freshToken);
        }
      } catch (error) {
        console.error('Failed to refresh token:', error);
      }
    }

    return response;
  }, [authToken, sessionAccessToken, userId, getAccessToken]);

  // Wrapper for getAccessToken to match old Clerk interface.
  // Prefer tokens already in React state / the Supabase session object so
  // callers (especially the interview WS auth on every reconnect) don't
  // trigger a refresh round-trip that can briefly return null and spin a
  // close -> auto-reconnect loop.
  const getToken = async (_options?: { skipCache?: boolean }): Promise<string | null> => {
    if (authToken) return authToken;
    if (sessionAccessToken) return sessionAccessToken;
    return await getAccessToken();
  };

  /** Stable identity for useEffect/useCallback deps - plain object was new every render → downstream loops. */
  const authUser: AuthUser | null = useMemo(() => {
    if (!user) return null;
    return {
      userId: user.id,
      email: user.email,
      firstName: user.user_metadata?.first_name,
      lastName: user.user_metadata?.last_name,
      imageUrl: user.user_metadata?.avatar_url,
    };
  }, [
    user?.id,
    user?.email,
    user?.user_metadata?.first_name,
    user?.user_metadata?.last_name,
    user?.user_metadata?.avatar_url,
  ]);

  return {
    isSignedIn: !!user && !loading,
    isLoading: loading,
    user: authUser,
    authToken,
    authReady,
    getAuthHeaders,
    apiCall,
    getToken,
    signOut,
  };
};
